import { HideOutlined, ShowOutlined } from '@icon';
import hoistNonReactStatics from 'hoist-non-react-statics';
import React from 'react';
import Form from '../../form';
import { TFormVariant } from '../../form/Form.types';
import InputSkeleton, { InputSkeletonProps } from '../components/input-skeleton';
import { fireInputChange } from '../inputs.utils';
import { InputSC, InputSkeletonSC } from './text-field.styled';
import classnames from 'classnames';
import TextArea from './text-area';

type TSkeletonProps = Pick<InputSkeletonProps, 'left' | 'right' | 'outlined'>;

interface InputProps
  extends React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
    TSkeletonProps {
  clearable?: boolean;
  variant?: TFormVariant;
  /**
   * Input states
   */
  disabled?: boolean;
  readonly?: boolean;
  error?: boolean;
  borderLess?: InputSkeletonProps['borderLess'];
  style?: React.CSSProperties;
  className?: string;
  onClearClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  permanentPlaceholder?: boolean;
}

const Input = (props: InputProps, ref?: React.Ref<HTMLInputElement | null>) => {
  const {
    style,
    clearable,
    disabled,
    readonly,
    error,
    className,
    left,
    right,
    borderLess,
    type,
    variant,
    required,
    onChange,
    onClearClick,
    permanentPlaceholder,
    placeholder,
    outlined,
    ...rest
  } = props;

  const settings = Form.useFormSettings({ variant, disabled });

  const inputRef = React.useRef<HTMLInputElement>(null);
  React.useImperativeHandle(ref, () => inputRef.current);

  const [focused, setFocused] = React.useState(false);
  const [showPassword, setShowPassword] = React.useState(false);
  const hasValue =
    (props.value && typeof props.value === 'string') || Array.isArray(props.value)
      ? props.value.length > 0
      : false;

  const onFocus: React.FocusEventHandler<HTMLInputElement> = (e) => {
    props.onFocus?.(e);
    setFocused(true);
  };

  const onBlur: React.FocusEventHandler<HTMLInputElement> = (e) => {
    props.onBlur?.(e);
    setFocused(false);
  };

  const onClick: React.MouseEventHandler<any> = (e) => {
    rest.onClick?.(e);
    inputRef.current?.focus();
  };

  return (
    <InputSkeletonSC
      outlined={outlined}
      focused={focused}
      onClick={onClick}
      style={style}
      error={error}
      readonly={readonly}
      className={classnames('ui-textfield', className)}
      borderLess={borderLess}
      variant={settings.variant}
      disabled={settings.disabled}
      left={left}
      right={
        type === 'password' ? (
          <InputSkeleton.Action
            Icon={showPassword ? HideOutlined : ShowOutlined}
            onClick={() => setShowPassword((s) => !s)}
            variant={settings.variant}
            tabIndex={-1}
          />
        ) : clearable && hasValue && !disabled && !readonly ? (
          <InputSkeleton.ClearAction
            variant={settings.variant}
            onClearPress={(e) => {
              fireInputChange(inputRef.current, 'input', '');
              onClearClick?.(e);
            }}
            right={right}
          />
        ) : (
          right
        )
      }
    >
      {permanentPlaceholder ? (
        <InputSkeleton.Placeholder
          placeholder={placeholder}
          value={props.value as any}
          variant={settings.variant}
          style={{ top: 8 }}
        />
      ) : null}
      <InputSC
        {...rest}
        onClick={onClick}
        ref={inputRef}
        onBlur={onBlur}
        onFocus={onFocus}
        disabled={disabled}
        onChange={onChange}
        type={showPassword ? 'text' : type}
        readOnly={readonly}
        variant={settings.variant}
        placeholder={permanentPlaceholder ? undefined : placeholder}
      />
    </InputSkeletonSC>
  );
};

Input.Prefix = InputSkeleton.Prefix;
Input.Suffix = InputSkeleton.Suffix;
Input.Action = InputSkeleton.Action;
Input.ClearAction = InputSkeleton.ClearAction;
Input.Icon = InputSkeleton.Icon;
Input.TextArea = TextArea;

export default hoistNonReactStatics(React.forwardRef(Input), Input);
